import {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  useReducer,
} from "react";
import { useLocation, useNavigate } from "react-router";
import { AgenticAPI } from "@/services/api";
import {
  ActivityLog,
  type ActivityLogRefType,
  type ActivityLogItem,
} from "@/components/activity/ActivityLog";
import { FilesList, type FilesListRef } from "@/components/documents/FilesList";
import { Chat, type ChatMessage } from "@/components/chat/Chat";
import { PageLayout } from "@/components/PageLayout";
import { UploadDialog } from "@/components/documents/UploadDialog";
import type {
  PodInstance as PodInstanceType,
  Document as AgenticDocument,
  PodTemplate as ApiPodTemplate,
} from "@/services/api/agentic/types";
import type { Document } from "@/services/api/documents/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useWebSocket } from "../../context/WebSocketContext";
import {
  faClockRotateLeft,
  faFolder,
  faListCheck,
  faDatabase,
  faLayerGroup,
  faBook,
  faHashtag,
  faChartLine,
  faComments,
  faClock,
  faFileAlt,
  faCheckSquare,
  faBriefcase,
  faExclamationTriangle,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { Tasks } from "./Tasks";
import { Data } from "./Data";
import { Actions } from "./Actions";
import { usePodInstanceTabNotifications } from "@/hooks/usePodInstanceTabNotifications";
import { WS_CHANNELS } from "@/utils/websocket";
import { Portfolios } from "./Portfolios";
import { Overview } from "./Overview";
import type { DataTable, DataTableSummary, DataItem } from "@/components/pods/Data";
import type { PortfolioTable } from "@/components/pods/Portfolios";
import type { Task } from "@/components/pods/Tasks";
import WorkerCardDialog from "@/components/worker/WorkerCardDialog";
import { motion, AnimatePresence } from "motion/react";
import { getPodTemplate } from "@/services/api/agentic";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { useAuth } from "@/context/AuthContext";
import WorkerAvatar from "../worker/WorkerAvatar";
import { ConfirmDeleteDialog } from "@/components/ui/dialog/ConfirmDeleteDialog";
import { Button } from "@/components/ui/button/Button";
import { useNotification } from "@/context/NotificationContext";
import { formatTimeDuration } from "@/utils/datetime";

// Define the tabs available in the instance interface
export const TABS = {
  OVERVIEW: "overview",
  CHAT: "channel",
  ACTIVITY: "activity",
  DOCUMENTS: "documents",
  TASKS: "tasks",
  DATA: "data",
  PORTFOLIOS: "portfolios",
} as const;

type TabId = (typeof TABS)[keyof typeof TABS];

// Define the state type for instance data
type InstanceState = {
  instance: PodInstanceType | null;
  loading: boolean;
  error: string | null;
  refreshCounter: number;
};

// Define the action types
type InstanceAction =
  | { type: "FETCH_START" }
  | { type: "FETCH_SUCCESS"; payload: PodInstanceType }
  | { type: "FETCH_ERROR"; payload: string }
  | { type: "REFRESH" };

// Define the reducer function
function instanceReducer(
  state: InstanceState,
  action: InstanceAction
): InstanceState {
  switch (action.type) {
    case "FETCH_START":
      return { ...state, loading: true, error: null };
    case "FETCH_SUCCESS":
      return { ...state, loading: false, instance: action.payload };
    case "FETCH_ERROR":
      return { ...state, loading: false, error: action.payload };
    case "REFRESH":
      return {
        ...state,
        loading: true,
        refreshCounter: state.refreshCounter + 1,
      };
    default:
      return state;
  }
}

interface InstanceComponentProps {
  id: string;
  template: string;
  showHeader?: boolean;
  initialActiveTab?: TabId;
}

interface PortfolioField {
  name: string;
  label?: string;
  value: any;
  source_file?:
    | {
        label: string;
        file_id: string;
      }
    | string
    | null;
}

// API Tab Structure for Pod Instance
interface PodInstanceApiTab {
  name: string;
  type: string; // This will be the new ID for tabs, e.g., "dashboard", "channel"
  icon?: string; // e.g., "fa-chart-line"
  color?: string; // e.g., "#3B82F6"
}

// Extend ApiPodTemplate for pod_instance_tabs
interface PodTemplateWithInstanceTabs extends ApiPodTemplate {
  pod_settings?: {
    pod_instance_tabs?: PodInstanceApiTab[];
    // other pod_settings if needed
  };
}

export function PodInstance({
  id,
  template,
  initialActiveTab = TABS.OVERVIEW,
}: InstanceComponentProps) {
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();

  // Initialize state with reducer
  const initialStateForReducer: InstanceState = {
    instance: null,
    loading: true,
    error: null,
    refreshCounter: 0,
  };

  const [state, dispatch] = useReducer(instanceReducer, initialStateForReducer);
  const [activeTab, setActiveTab] = useState<string | undefined>(undefined);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [selectedWorkerId, setSelectedWorkerId] = useState<string | null>(null);
  const [isWorkerCardOpen, setIsWorkerCardOpen] = useState(false);
  const [recentActivities, setRecentActivities] = useState<ActivityLogItem[]>(
    []
  );
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  // State for Pod Template
  const [podTemplate, setPodTemplate] =
    useState<PodTemplateWithInstanceTabs | null>(null);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState<boolean>(true);
  const [templateError, setTemplateError] = useState<Error | null>(null);

  // State for Instance Stats
  const [instanceStats, setInstanceStats] = useState<any>(null);

  const location = useLocation();
  const navigate = useNavigate();
  const { status, subscribe, unsubscribe, lastMessage, onMessage } =
    useWebSocket();

  // Refs for child components
  const documentsListRef = useRef<FilesListRef>(null);
  const activityLogRef = useRef<ActivityLogRefType>(null);

  // Refs to track component lifecycle and prevent duplicate requests
  const isMounted = useRef(true);
  const isLoadingRef = useRef(false);

  // Use the new hook for tab notifications
  const { tabNotifications, clearTabNotification } =
    usePodInstanceTabNotifications(id, activeTab || "");

  // Icon mapping for dynamic icon rendering from API
  const iconMap: Record<string, IconDefinition> = {
    "fa-chart-line": faChartLine,
    "fa-comments": faComments,
    "fa-clock": faClock,
    "fa-file-alt": faFileAlt,
    "fa-check-square": faCheckSquare,
    "fa-database": faDatabase,
    "fa-briefcase": faBriefcase,
    "fa-exclamation-triangle": faExclamationTriangle,
    // Add fallbacks or map existing icons if API names differ
    "fa-book": faBook, // Overview fallback
    "fa-hashtag": faHashtag, // Channel fallback
    "fa-clock-rotate-left": faClockRotateLeft, // Activity fallback
    "fa-folder": faFolder, // Documents fallback
    "fa-list-check": faListCheck, // Tasks fallback
    "fa-layer-group": faLayerGroup, // Portfolios fallback
  };

  const getIconByName = (name?: string): IconDefinition | undefined => {
    if (!name) return undefined;
    return iconMap[name];
  };

  // Fetch Pod Template
  useEffect(() => {
    if (template) {
      setIsLoadingTemplate(true);
      setTemplateError(null);
      getPodTemplate(template)
        .then((data) => {
          setPodTemplate(data as PodTemplateWithInstanceTabs);
        })
        .catch((err) => {
          console.error("Failed to fetch pod template for instance:", err);
          setTemplateError(err);
        })
        .finally(() => {
          setIsLoadingTemplate(false);
        });
    }
  }, [template]);

  // Custom fetch functions for instance resources, now defined inside the component
  const fetchCallToActionsByTemplate = useCallback(
    async (templateId: string): Promise<any[]> => {
      const response = await AgenticAPI.getCallToActionsByTemplate(templateId, {
        limit: 500,
      });
      // Filter to only show active CTAs (live and processing), exclude completed and cancelled
      const activeCtas = Array.isArray(response)
        ? response.filter(
            (cta: any) => cta.status === "live" || cta.status === "processing"
          )
        : [];
      return activeCtas;
    },
    []
  );

  const fetchPodInstancesForTemplate = useCallback(
    async (
      templateId: string
    ): Promise<Array<{ id: string; name: string }>> => {
      const response = await AgenticAPI.getPodInstances({
        template_id: templateId,
        limit: 500,
      });
      const instances = Array.isArray(response) ? response : [];
      return instances.map((instance: any) => ({
        id: instance.id || instance._id,
        name: instance.name,
      }));
    },
    []
  );

  const fetchInstanceDocuments = useCallback(
    async (instanceIdParam?: string): Promise<Document[]> => {
      const currentInstanceId = instanceIdParam || id;
      if (!currentInstanceId) return [];
      const response = await AgenticAPI.getAgenticDocuments(currentInstanceId);
      return (Array.isArray(response) ? response : []).map(
        (doc: AgenticDocument) => ({
          // Required fields
          document_id: doc._id,
          metadata: doc.metadata || {},
          chunk_count: 0,
          landscape: false,

          // Optional fields
          filename:
            doc.name ||
            doc.original_filename ||
            doc.display_name ||
            "Unnamed Document",
          file_type: doc.content_type || doc.mime_type || "unknown",
          upload_date: doc.created_at,
          user_id: doc.uploaded_by?.id || "unknown",
          resource_type: doc.resource_type || "instance",
          resource_id: doc.resource_id || currentInstanceId,
          as_at: doc.as_at || doc.created_at,
          display_name: doc.display_name,
          original_filename: doc.original_filename,
          size: doc.size,
          mime_type: doc.mime_type,
          uploaded_by: doc.uploaded_by
            ? { id: doc.uploaded_by.id, name: doc.uploaded_by.name }
            : undefined,

          // Additional fields from API response
          name: doc.name,
          content_type: doc.content_type,
          status: doc.status,
          azure_blob_path: doc.azure_blob_path,
          vector_store_id: doc.vector_store_id,
          updated_at: doc.updated_at,

          // URL fields needed for download/viewing
          url: doc.url,
          render_url: doc.render_url,
        })
      );
    },
    [id]
  );

  const fetchInstanceActivities = useCallback(
    async (
      entityIdParam: string,
      limit?: number
    ): Promise<ActivityLogItem[]> => {
      const currentEntityId = entityIdParam || id;
      const response = await AgenticAPI.getAgenticActivities(currentEntityId);
      const activities = Array.isArray(response) ? response : [];
      const mapped = activities.slice(0, limit).map((activity) => ({
        id: activity._id,
        description: activity.description,
        timestamp: activity.timestamp,
        worker_id: activity.assignee?.id,
        metadata: activity.metadata,
        actor: activity.assignee ? { name: activity.assignee.name } : undefined,
      }));
      // console.log("fetchInstanceActivities called:", { currentEntityId, limit, response, mapped }); // Keep logs minimal
      return mapped;
    },
    [id]
  );

  const fetchInstanceTasks = useCallback(
    async (instanceIdParam: string): Promise<Task[]> => {
      const currentInstanceId = instanceIdParam || id;
      const response =
        await AgenticAPI.getAgenticWorkflowTasks(currentInstanceId);
      return Array.isArray(response) ? response : [];
    },
    [id]
  );

  const fetchInstanceDatasetSummaries = useCallback(
    async (instanceIdParam: string): Promise<DataTableSummary[]> => {
      const currentInstanceId = instanceIdParam || id;
      const response = await AgenticAPI.getAgenticDatasetSummaries(currentInstanceId);
      return (Array.isArray(response) ? response : []).map((dataset) => ({
        id: dataset._id,
        name: dataset.name,
        summary: dataset.summary,
        fieldCount: dataset.field_count,
        filledFieldCount: dataset.filled_field_count,
        createdAt: dataset.created_at,
      }));
    },
    [id]
  );

  const fetchDataset = useCallback(
    async (datasetId: string): Promise<DataTable> => {
      const response = await AgenticAPI.getAgenticDataset(datasetId);
      return {
        id: response._id,
        name: response.name,
        summary: response.summary,
        fieldCount: response.data?.length || 0,
        createdAt: response.created_at,
        data: (response.data || []).map((field) => {
          let itemValue: string | object;
          if (field.value === null) {
            itemValue = "";
          } else if (typeof field.value === "number") {
            itemValue = String(field.value);
          } else if (Array.isArray(field.value)) {
            itemValue = field.value;
          } else {
            itemValue = field.value;
          }
          const typedField = field as {
            name: string;
            value: any;
            source_file: string | { file_id: string; label: string } | null;
            source_reference?: string;
            confidence?: number;
            history: any[];
            label?: string;
          };

          return {
            id: typedField.name,
            key: typedField.name,
            value: itemValue,
            source_file: typedField.source_file || "Unknown Source",
            source_reference: typedField.source_reference,
            confidence: typedField.confidence,
            history: (typedField.history || []).map((h) => ({
              ...h,
              source_file: h.source_file || "Unknown Source",
            })),
            updatedAt: response.updated_at || new Date().toISOString(),
            label: typedField.label || typedField.name,
          } as DataItem;
        }),
      };
    },
    []
  );

  const updateDatasetField = useCallback(
    async (datasetId: string, fieldName: string, newValue: string, updatedBy: string, note?: string): Promise<DataTable> => {
      const response = await AgenticAPI.updateAgenticDatasetField(datasetId, fieldName, newValue, updatedBy, note);
      return {
        id: response._id,
        name: response.name,
        summary: response.summary,
        fieldCount: response.data?.length || 0,
        createdAt: response.created_at,
        data: (response.data || []).map((field) => {
          let itemValue: string | object;
          if (field.value === null) {
            itemValue = "";
          } else if (typeof field.value === "number") {
            itemValue = String(field.value);
          } else if (Array.isArray(field.value)) {
            itemValue = field.value;
          } else {
            itemValue = field.value;
          }
          const typedField = field as {
            name: string;
            value: any;
            source_file: string | { file_id: string; label: string } | null;
            source_reference?: string;
            confidence?: number;
            history: any[];
            label?: string;
          };

          return {
            id: typedField.name,
            key: typedField.name,
            value: itemValue,
            source_file: typedField.source_file || "Unknown Source",
            source_reference: typedField.source_reference,
            confidence: typedField.confidence,
            history: (typedField.history || []).map((h) => ({
              ...h,
              source_file: h.source_file || "Unknown Source",
            })),
            updatedAt: response.updated_at || new Date().toISOString(),
            label: typedField.label || typedField.name,
          } as DataItem;
        }),
      };
    },
    []
  );

  const deleteDataset = useCallback(
    async (datasetId: string): Promise<void> => {
      await AgenticAPI.deleteAgenticDataset(datasetId);
    },
    []
  );


  // Function to load instance data
  const loadInstance = useCallback(async () => {
    if (isLoadingRef.current || !isMounted.current) return;

    isLoadingRef.current = true;
    dispatch({ type: "FETCH_START" });

    try {
      const [instanceData, statsData] = await Promise.all([
        AgenticAPI.getPodInstance(id),
        AgenticAPI.getPodInstanceStats(id),
      ]);

      if (isMounted.current) {
        dispatch({ type: "FETCH_SUCCESS", payload: instanceData });
        setInstanceStats(statsData);
      }
    } catch (err) {
      console.error("Error loading instance:", err);
      if (isMounted.current) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to load instance data";
        dispatch({ type: "FETCH_ERROR", payload: errorMessage });
      }
    } finally {
      isLoadingRef.current = false;
    }
  }, [id]);

  // Memoize the fetch history handler
  const handleFetchChatHistory = useCallback(
    async (recipientId: string): Promise<ChatMessage[]> => {
      const response = await AgenticAPI.getAgenticChatMessages(recipientId);
      return (Array.isArray(response) ? response : []).map((msg) => ({
        id: msg._id,
        content: msg.content,
        sender: {
          id: msg.sender.id,
          name: msg.sender.name,
          type: msg.sender.type,
        },
        timestamp: new Date(msg.timestamp).getTime(),
        actions: msg.actions,
        attachments: msg.attachments,
      }));
    },
    []
  );

  // Memoize the send message handler
  const handleSendMessage = useCallback(
    async (
      recipientId: string,
      data: {
        content: string;
        role: string;
        attachments?: Array<{
          type: "document";
          document_id: string;
          display_name?: string;
          mime_type?: string;
          size?: number;
        }>;
      }
    ) => {
      const response = await AgenticAPI.createAgenticChatMessage({
        pod_instance_id: recipientId,
        workflow_task_id: "instance_chat",
        content: data.content,
        message_type: "chat",
        actions: [],
        sender: {
          id: user?.id || "current-user",
          type: data.role,
          name: user?.fullname || "You",
          image: user?.image,
        },
        attachments: data.attachments || [],
      });
      return response;
    },
    [user]
  );

  // Cleanup on unmount
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Effect to handle initial load and refreshes
  useEffect(() => {
    loadInstance();
  }, [loadInstance, state.refreshCounter]);

  // WebSocket subscription for pod instance updates
  useEffect(() => {
    const channel = WS_CHANNELS.POD_INSTANCES;
    subscribe(channel);

    return () => {
      unsubscribe(channel);
    };
  }, [subscribe, unsubscribe]);

  // Handle pod instance WebSocket messages
  useEffect(() => {
    const message: any = lastMessage?.get(WS_CHANNELS.POD_INSTANCES);

    if (message && message.operation === "update" && message.full_document) {
      const instanceData = message.full_document;

      // Check if this update is for our current instance
      if (instanceData._id === id && isMounted.current) {
        dispatch({ type: "FETCH_SUCCESS", payload: instanceData });
      }
    }
  }, [lastMessage, id]);

  // Initialize activeTab based on URL, then prop, then first available tab
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabFromUrl = searchParams.get("tab");

    const availableApiTabs = podTemplate?.pod_settings?.pod_instance_tabs;

    if (
      tabFromUrl &&
      availableApiTabs?.find((t: PodInstanceApiTab) => t.type === tabFromUrl)
    ) {
      setActiveTab(tabFromUrl);
      return;
    }

    let tabToSet: string | undefined = undefined;

    if (initialActiveTab) {
      if (
        initialActiveTab === TABS.OVERVIEW &&
        availableApiTabs?.find((t: PodInstanceApiTab) => t.type === "dashboard")
      ) {
        tabToSet = "dashboard";
      } else if (
        availableApiTabs?.find(
          (t: PodInstanceApiTab) => t.type === initialActiveTab
        )
      ) {
        tabToSet = initialActiveTab;
      }
    }

    if (!tabToSet && availableApiTabs && availableApiTabs.length > 0) {
      tabToSet = availableApiTabs[0].type;
    }

    if (tabToSet) {
      setActiveTab(tabToSet);
      // Update URL if setting from prop or default, and not from URL initially
      if (!tabFromUrl || tabFromUrl !== tabToSet) {
        const newSearchParams = new URLSearchParams(location.search);
        newSearchParams.set("tab", tabToSet);
        navigate(`${location.pathname}?${newSearchParams.toString()}`, {
          replace: true,
        });
      }
    }
  }, [
    location.search,
    location.pathname,
    navigate,
    initialActiveTab,
    podTemplate,
  ]);

  // Update URL when tab changes
  const handleTabChange = (tabApiType: string) => {
    setActiveTab(tabApiType);
    clearTabNotification(tabApiType);
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("tab", tabApiType);
    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });
  };

  const handleCloseUploadDialog = () => {
    setIsUploadDialogOpen(false);
  };

  // Functions to handle WorkerCardDialog
  const handleOpenWorkerCardDialog = (workerId: string) => {
    setSelectedWorkerId(workerId);
    setIsWorkerCardOpen(true);
  };

  const handleCloseWorkerCardDialog = () => {
    setSelectedWorkerId(null);
    setIsWorkerCardOpen(false);
  };

  // Delete instance handler
  const handleDeleteInstance = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await AgenticAPI.deletePodInstance(id);
      setIsDeleteDialogOpen(false);
      showSuccess("Instance deleted successfully");
      // Navigate back to pod template
      navigate(`/pod/${template}`);
    } catch (error) {
      console.error("Failed to delete instance:", error);
      showError("Failed to delete instance", "Please try again");
      setIsDeleteDialogOpen(false);
    }
  };

  // Create a function to upload instance documents
  const uploadToInstance = async (file: File): Promise<Document> => {
    console.warn(
      "uploadToInstance needs verification against actual AgenticAPI.uploadPodInstanceDocument method and types"
    );
    const tempDocId = Date.now().toString();
    const instanceDoc: AgenticDocument = {
      _id: tempDocId,
      pod_instance_id: id,
      workflow_task_id: "upload_task",
      metadata: { description: `Uploaded document for instance: ${file.name}` },
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),

      // Optional fields
      original_filename: file.name,
      display_name: file.name,
      name: file.name,
      mime_type: file.type,
      content_type: file.type,
      size: file.size,
      storage_path: `uploads/${file.name}`,
      render_url: null,
      is_output: false,
      source_files: [],
      uploaded_at: new Date().toISOString(),
      uploaded_by: {
        id: user?.id || "current-user",
        type: "user",
        name: user?.fullname || "Current User",
      },
    };

    return {
      document_id: instanceDoc._id,
      metadata: instanceDoc.metadata,
      chunk_count: 0,
      landscape: false,

      // Optional fields
      filename:
        instanceDoc.name ||
        instanceDoc.original_filename ||
        instanceDoc.display_name ||
        "Unnamed Document",
      file_type: instanceDoc.content_type || instanceDoc.mime_type || "unknown",
      upload_date: instanceDoc.uploaded_at || instanceDoc.created_at,
      user_id: instanceDoc.uploaded_by?.id || "unknown",
      resource_type: "instance",
      resource_id: instanceDoc.pod_instance_id,
      as_at: instanceDoc.created_at,
      display_name: instanceDoc.display_name,
      original_filename: instanceDoc.original_filename,
      size: instanceDoc.size,
      mime_type: instanceDoc.mime_type,
      name: instanceDoc.name,
      content_type: instanceDoc.content_type,
      uploaded_by: instanceDoc.uploaded_by
        ? { id: instanceDoc.uploaded_by.id, name: instanceDoc.uploaded_by.name }
        : undefined,
    };
  };

  const handleUploadComplete = () => {
    setIsUploadDialogOpen(false);
    if (activeTab === TABS.DOCUMENTS && documentsListRef.current) {
      documentsListRef.current.refreshDocuments();
    }
  };

  // Memoize tab content
  const overviewContent = useMemo(() => {
    if (!state.instance) return null;
    return (
      <div className="flex h-full min-h-0 flex-col overflow-hidden">
        <Overview
          instanceId={id}
          startDate={state.instance.created_at}
          latestActionDate={state.instance.updated_at}
          expectedCompletionDate={
            state.instance.metadata?.expected_completion_date || undefined
          }
          recentActivities={recentActivities}
        />
      </div>
    );
  }, [state.instance, id, recentActivities]);

  const chatContent = useMemo(() => {
    if (!state.instance) return null;
    return (
      <div className="flex h-full flex-grow">
        <Chat
          recipientId={id}
          recipientName={state.instance.name}
          recipientAvatar={state.instance.name.substring(0, 2)}
          sendMessage={handleSendMessage}
          fetchHistory={handleFetchChatHistory}
          chatChannel={WS_CHANNELS.CHAT}
          systemTaskChannel={WS_CHANNELS.SYSTEM_TASKS}
        />
      </div>
    );
  }, [state.instance, id, handleSendMessage, handleFetchChatHistory]);

  const activityContent = useMemo(() => {
    if (!state.instance) return null;
    return (
      <div className="flex h-full flex-grow">
        <ActivityLog
          ref={activityLogRef}
          entityId={id}
          entityType="instance"
          title="Instance Activity"
          fetchActivityLogs={fetchInstanceActivities}
          websocketMessageType={WS_CHANNELS.ACTIVITY}
        />
      </div>
    );
  }, [state.instance, id, activityLogRef, fetchInstanceActivities]);

  const documentsContent = useMemo(() => {
    if (!state.instance) return null;
    return (
      <div className="flex h-full flex-grow">
        <div className="p-4 w-full">
          <FilesList
            ref={documentsListRef}
            entityId={id}
            entityType="instance"
            title="Documents"
            fetchDocuments={fetchInstanceDocuments}
            websocketMessageType={WS_CHANNELS.DOCUMENTS}
          />
        </div>
      </div>
    );
  }, [state.instance, id, documentsListRef, fetchInstanceDocuments]);

  const tasksContent = useMemo(() => {
    if (!state.instance) return null;
    return (
      <div className="flex h-full flex-grow">
        <Tasks
          entityId={id}
          entityType="instance"
          fetchTasks={fetchInstanceTasks}
          podTemplate={podTemplate || undefined}
        />
      </div>
    );
  }, [state.instance, id, fetchInstanceTasks, podTemplate]);

  const dataContent = useMemo(() => {
    if (!state.instance) return null;
    return (
      <div className="flex h-full flex-grow">
        <Data
          entityId={id}
          entityType="instance"
          fetchSummaries={fetchInstanceDatasetSummaries}
          fetchDataset={fetchDataset}
          updateField={updateDatasetField}
          deleteDataset={deleteDataset}
          websocketMessageType={WS_CHANNELS.DATA}
        />
      </div>
    );
  }, [state.instance, id, fetchInstanceDatasetSummaries, fetchDataset, updateDatasetField, deleteDataset]);

  const portfoliosContent = useMemo(() => {
    if (!state.instance) return null;
    return (
      <div className="flex h-full flex-grow">
        <Portfolios
          entityId={id}
          entityType="instance"
          websocketMessageType={WS_CHANNELS.PORTFOLIOS}
        />
      </div>
    );
  }, [state.instance, id]);

  const actionsContent = useMemo(() => {
    if (!state.instance || !template) return null;
    return (
      <div className="flex h-full flex-grow">
        <Actions
          templateId={template}
          fetchCallToActions={fetchCallToActionsByTemplate}
          fetchPodInstances={fetchPodInstancesForTemplate}
          podTemplate={podTemplate || undefined}
        />
      </div>
    );
  }, [
    state.instance,
    template,
    fetchCallToActionsByTemplate,
    fetchPodInstancesForTemplate,
    podTemplate,
  ]);

  // Map API tab types to their corresponding content components
  const contentComponentMap: Record<string, React.ReactNode> = useMemo(
    () => ({
      dashboard: overviewContent,
      channel: chatContent,
      activity: activityContent,
      documents: documentsContent,
      tasks: tasksContent,
      data: dataContent,
      portfolios: portfoliosContent,
      actions: actionsContent,
    }),
    [
      overviewContent,
      chatContent,
      activityContent,
      documentsContent,
      tasksContent,
      dataContent,
      portfoliosContent,
      actionsContent,
    ]
  );

  const dynamicTabContent = useMemo(() => {
    if (!podTemplate?.pod_settings?.pod_instance_tabs) {
      return [];
    }

    return podTemplate.pod_settings.pod_instance_tabs
      .map((apiTab: PodInstanceApiTab) => {
        // Check if the tab type exists in the map (it may return null during initial render)
        if (!(apiTab.type in contentComponentMap)) {
          console.warn(
            `No content component found for tab type: ${apiTab.type}`
          );
          return null;
        }
        const content = contentComponentMap[apiTab.type];

        const iconDefinition = getIconByName(apiTab.icon);
        let displayIcon: React.ReactNode;

        if (iconDefinition) {
          displayIcon = <FontAwesomeIcon icon={iconDefinition} fixedWidth />;
        } else {
          // Fallback to old icon definitions if API icon is missing or not in map
          // This requires a mapping from apiTab.type to the old icon components
          // For simplicity, we'll use a generic fallback or specific ones if easily mapped.
          // Example: if apiTab.type is 'dashboard', use faBook as a fallback.
          // This part might need more refinement based on desired fallback behavior.
          const fallbackIconMap: Record<string, React.ReactNode> = {
            dashboard: <FontAwesomeIcon icon={faBook} fixedWidth />,
            channel: <FontAwesomeIcon icon={faHashtag} fixedWidth />,
            activity: <FontAwesomeIcon icon={faClockRotateLeft} fixedWidth />,
            documents: <FontAwesomeIcon icon={faFolder} fixedWidth />,
            tasks: <FontAwesomeIcon icon={faListCheck} fixedWidth />,
            data: <FontAwesomeIcon icon={faDatabase} fixedWidth />,
            portfolios: <FontAwesomeIcon icon={faLayerGroup} fixedWidth />,
            actions: (
              <FontAwesomeIcon icon={faExclamationTriangle} fixedWidth />
            ),
          };
          displayIcon = fallbackIconMap[apiTab.type] || (
            <FontAwesomeIcon icon={faBook} fixedWidth />
          ); // Default fallback
        }

        return {
          id: apiTab.type, // Use API type as ID
          label: apiTab.name,
          icon: displayIcon,
          content: content,
          showNotification: tabNotifications[apiTab.type],
          currentIcon: apiTab.icon, // Pass API icon name string
        };
      })
      .filter((tab): tab is NonNullable<typeof tab> => tab !== null);
  }, [podTemplate, contentComponentMap, tabNotifications, getIconByName]);

  // Add effect to seed recent activities when Overview ('dashboard') is active
  useEffect(() => {
    // Only poll when the Overview ('dashboard') tab is active
    if (activeTab !== "dashboard") {
      return;
    }

    let cancelled = false;

    const fetchRecentActivities = async () => {
      try {
        if (cancelled) return;
        const activities = await fetchInstanceActivities(id, 5);
        setRecentActivities(activities);
      } catch (error) {
        console.error("Error fetching recent activities:", error);
      }
    };

    // Seed once on activation
    fetchRecentActivities();

    return () => {
      cancelled = true;
    };
  }, [id, fetchInstanceActivities, activeTab]);

  // Live-update the Overview ticker via Activity websocket when Overview is active
  useEffect(() => {
    if (activeTab !== "dashboard") return;

    const off = onMessage(WS_CHANNELS.ACTIVITY, (raw: any) => {
      try {
        const base = typeof raw === "string" ? JSON.parse(raw) : raw;
        const msg = base && base.payload ? base.payload : base;
        if (!msg || !msg.operation || !msg.full_document) return;
        if (msg.operation !== "insert") return;
        const doc = msg.full_document;
        if (String(doc.pod_instance_id) !== String(id)) return;

        const newItem: ActivityLogItem = {
          id: doc._id,
          description: doc.description,
          timestamp: doc.timestamp,
          worker_id: doc.assignee?.id,
          metadata: doc.metadata,
          actor: doc.assignee ? { name: doc.assignee.name } : undefined,
        };

        setRecentActivities((prev) => {
          const existing = new Set(prev.map((a) => a.id));
          if (existing.has(newItem.id)) return prev;
          return [newItem, ...prev].slice(0, 5);
        });
      } catch (e) {
        console.error(
          "[Overview ticker] Error handling activity WS message",
          e
        );
      }
    });

    return () => {
      if (typeof off === "function") off();
    };
  }, [activeTab, id, onMessage]);

  // NEW LOADING AND ERROR HANDLING LOGIC
  if (isLoadingTemplate) {
    return <div className="p-8 text-slate-500 dark:text-slate-400">Loading template configuration...</div>;
  }

  if (templateError) {
    return (
      <div className="p-8 text-red-600 dark:text-red-400">
        Error loading pod template: {templateError.message}
      </div>
    );
  }

  // If template is fetched but somehow null (should not happen if logic is correct and template is required)
  if (!podTemplate) {
    return <div className="p-8">Pod template configuration not available.</div>;
  }

  // Instance loading and error states
  if (state.loading) {
    return <div className="p-8">Loading instance details...</div>;
  }

  if (state.error) {
    return <div className="p-8">Error loading instance: {state.error}</div>;
  }

  if (!state.instance) {
    return <div className="p-8">Instance not found.</div>;
  }
  // END NEW LOADING AND ERROR HANDLING LOGIC

  return (
    <>
      <PageLayout
        title={state.instance.name}
        tabs={dynamicTabContent}
        activeTab={activeTab}
        backLink={{
          to: `/pod/${template}`,
          label: `Back to ${podTemplate?.name || "Pod"}`,
        }}
        onTabChange={handleTabChange}
        staticTabs={true}
        disableIconEdit={true}
        tabRightSlot={
          <Button
            dropdown={true}
            dropdownItems={[
              {
                name: "Delete",
                onClick: () => handleDeleteInstance(),
              },
            ]}
            color="slate"
          />
        }
        subtitleComponent={
          <div className="flex flex-col gap-2 mt-0">
            <div className="flex items-center  text-sm ">
              <div className="flex text-xs flex-row gap-3 text-slate-900 dark:text-white uppercase items-center p-0 pr-3 font-mono ">
                <span className=" font-mono uppercase tracking-widest text-slate-500 dark:text-slate-400">
                  Status
                </span>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={state.instance.state.current_status}
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -5 }}
                    transition={{ duration: 0.2 }}
                  >
                    {state.instance.state.current_status}
                  </motion.div>
                </AnimatePresence>
              </div>
              <div className="flex text-xs flex-row items-center gap-3 text-slate-900 dark:text-white uppercase  font-mono pr-3">
                <span className=" font-mono uppercase tracking-widest text-slate-500 dark:text-slate-400">
                  Stage
                </span>
                <AnimatePresence mode="wait">
                  <motion.span
                    key={state.instance.state.current_stage}
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 5 }}
                    transition={{ duration: 0.2 }}
                  >
                    {state.instance.state.current_stage
                      ?.toUpperCase()
                      .replace(/_/g, " ") || "UNKNOWN"}
                  </motion.span>
                </AnimatePresence>
              </div>
              {/* Saved Minutes Display */}
              <div className="flex text-xs flex-row items-center gap-3 text-slate-900 dark:text-white uppercase font-mono pr-3">
                <span className=" font-mono uppercase tracking-widest text-slate-500 dark:text-slate-400">
                  Time Saved
                </span>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={state.instance.state.saved_minutes || 0}
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -5 }}
                    transition={{ duration: 0.2 }}
                  >
                    {formatTimeDuration(
                      state.instance.state.total_time_saved ||
                        state.instance.state.saved_minutes ||
                        0
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>
              {/* {state.instance.state.call_to_action && (
                <div className="flex flex-row gap-3 items-center p-0 pr-3 font-mono">
                  <span className="text-xs font-mono uppercase tracking-widest text-slate-500">
                    Action
                  </span>
                  <AnimatePresence mode="wait">
                    <motion.span
                      key={state.instance.state.call_to_action}
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 5 }}
                      transition={{ duration: 0.2 }}
                      className="text-gray-600 bg-gray-50 px-2 py-0.5 rounded-full"
                    >
                      {state.instance.state.call_to_action}
                    </motion.span>
                  </AnimatePresence>
                </div>
              )} */}
            </div>
          </div>
        }
        headerActionsComponent={
          <div className="flex items-center gap-3">
            <div className="flex flex-row-reverse group/avatars">
              {podTemplate?.worker_assignments?.map((assignment: any) => (
                <div
                  key={assignment.worker_id}
                  className="-mr-3 border-2 rounded-full border-white dark:border-slate-800 group-hover/avatars:mr-0 transition-all duration-200"
                >
                  <WorkerAvatar seed={assignment.name} size={20} />
                </div>
              ))}
            </div>
          </div>
        }
        headerDetailsComponent={
          <div className="flex items-center space-x-3">
            <div className="relative w-20 h-20">
              <svg className="w-full h-full" viewBox="0 0 36 36">
                <circle
                  cx="18"
                  cy="18"
                  r="15"
                  fill="none"
                  stroke="var(--progress-circle-bg)"
                  strokeWidth="4"
                />
                <circle
                  cx="18"
                  cy="18"
                  r="15"
                  fill="none"
                  stroke="var(--progress-circle-fg)"
                  strokeWidth="4"
                  strokeDasharray={`${
                    (instanceStats?.overall_progress || 0) * 100
                  } 100`}
                  strokeDashoffset="0"
                  transform="rotate(-90 18 18)"
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-base text-slate-900 dark:text-white font-mono font-semibold">
                  {Math.round((instanceStats?.overall_progress || 0) * 100)}%
                </span>
              </div>
            </div>
          </div>
        }
      />

      <UploadDialog
        isOpen={isUploadDialogOpen}
        onClose={handleCloseUploadDialog}
        title="Upload Instance Document"
        description={`Upload a document for ${state.instance.name}`}
        uploadFunction={uploadToInstance}
        onUploadComplete={handleUploadComplete}
      />
      {selectedWorkerId && (
        <WorkerCardDialog
          workerId={selectedWorkerId}
          open={isWorkerCardOpen}
          onClose={handleCloseWorkerCardDialog}
        />
      )}

      <ConfirmDeleteDialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Are you sure you want to delete?"
        description={`You are about to delete "${state.instance?.name}"?. This action cannot be undone and will permanently remove all associated data, documents, and activities.`}
        confirmLabel="Yes, Delete "
      />
    </>
  );
}
